import React, { useState, useEffect } from 'react'
import { useAuth } from './Auth/AuthProvider'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { motion, AnimatePresence } from 'framer-motion'
import * as teamService from '../services/teamService'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import { checkTeamDependencies } from '../services/dependencyService'

const { FiUsers, FiMail, FiClock, FiCheck, FiX, FiCopy, FiRefreshCw, FiShield, FiUser, FiPlus, FiAlertTriangle, FiEdit3, FiTrash2 } = FiIcons

const TeamManagement = () => {
  const { user, tenant } = useAuth()
  const [members, setMembers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [showEditMemberModal, setShowEditMemberModal] = useState(false)
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [memberDependencies, setMemberDependencies] = useState({})
  const [loading, setLoading] = useState(true)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load team members via Edge Function
      const membersData = await teamService.getMembers()
      setMembers(membersData)

      // Load pending invitations via Edge Function
      const invitationsData = await teamService.getInvitations()
      setInvitations(invitationsData)
    } catch (err) {
      console.error('Error loading data:', err)
      setError(err.message || 'Failed to load team data')
    } finally {
      setLoading(false)
    }
  }

  const handleInvite = async (email, role) => {
    try {
      setError(null)
      setSuccess(null)

      const invitation = await teamService.sendInvitation(email, role)

      setShowInviteForm(false)

      // Reload invitations
      await loadData()

      // Show success message - user can copy link via the copy button in the invitations list
      setSuccess(`Invitation sent to ${email}. Use the copy button below to share the invitation link.`)
    } catch (err) {
      console.error('Error sending invitation:', err)
      setError(err.message || 'Failed to send invitation')
    }
  }

  const handleEditMember = (member) => {
    setSelectedMember(member)
    setShowEditMemberModal(true)
  }

  const handleUpdateMemberRole = async (newRole) => {
    try {
      setError(null)
      await teamService.updateMemberRole(selectedMember.id, newRole)
      setSuccess('Member role updated successfully')
      setShowEditMemberModal(false)
      setSelectedMember(null)
      await loadData()
    } catch (err) {
      console.error('Error updating member role:', err)
      setError(err.message || 'Failed to update member role')
    }
  }

  const handleDeleteMember = async (member) => {
    // Prevent self-deletion
    if (member.email === user?.email) {
      setError('You cannot delete yourself from the team')
      return
    }

    setSelectedMember(member)
    const dependencies = await checkTeamDependencies(member.id)
    setMemberDependencies(dependencies)
    setShowDeleteMemberModal(true)
  }

  const confirmDeleteMember = async () => {
    try {
      setDeleteLoading(true)
      setError(null)
      await teamService.removeMember(selectedMember.id)
      setSuccess('Member removed successfully')
      setShowDeleteMemberModal(false)
      setSelectedMember(null)
      setMemberDependencies({})
      await loadData()
    } catch (err) {
      console.error('Error removing member:', err)
      setError('Failed to remove member. Please try again.')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return
    }

    try {
      setError(null)
      await teamService.cancelInvitation(invitationId)
      setSuccess('Invitation cancelled')
      await loadData()
    } catch (err) {
      console.error('Error canceling invitation:', err)
      setError(err.message || 'Failed to cancel invitation')
    }
  }

  const handleResendInvitation = async (invitationId) => {
    try {
      setError(null)
      await teamService.resendInvitation(invitationId)
      
      await loadData()
      
      // Show success message - user can copy link via the copy button
      setSuccess('Invitation resent! Use the copy button to share the new link.')
    } catch (err) {
      console.error('Error resending invitation:', err)
      setError(err.message || 'Failed to resend invitation')
    }
  }

  const handleCopyInvitationLink = async (token) => {
    try {
      const url = `${window.location.origin}/#/accept-invitation?token=${token}`
      await navigator.clipboard.writeText(url)
      setSuccess('Invitation link copied to clipboard!')
    } catch (err) {
      setError('Failed to copy link')
    }
  }

  const getRoleBadgeColor = (role) => {
    return role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading team...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600">Manage your organization's team members and invitations</p>
        </div>
        <button
          onClick={() => setShowInviteForm(true)}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} />
          <span>Invite Member</span>
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
        >
          <SafeIcon icon={FiAlertTriangle} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <SafeIcon icon={FiX} />
          </button>
        </motion.div>
      )}

      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3"
        >
          <SafeIcon icon={FiCheck} className="text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">{success}</p>
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-600 hover:text-green-800">
            <SafeIcon icon={FiX} />
          </button>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Members</p>
              <p className="text-3xl font-bold text-gray-900">{members.length}</p>
            </div>
            <SafeIcon icon={FiUsers} className="text-4xl text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Pending Invitations</p>
              <p className="text-3xl font-bold text-gray-900">{invitations.length}</p>
            </div>
            <SafeIcon icon={FiMail} className="text-4xl text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Admins</p>
              <p className="text-3xl font-bold text-gray-900">
                {members.filter(m => m.role === 'admin').length}
              </p>
            </div>
            <SafeIcon icon={FiShield} className="text-4xl text-purple-600" />
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex flex-col md:flex-row items-start md:items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 space-y-4 md:space-y-0"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {member.auth_users?.full_name?.charAt(0) || member.auth_users?.email?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {member.auth_users?.full_name || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-500">{member.auth_users?.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(member.role)}`}>
                    {member.role.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">
                    Joined {new Date(member.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleEditMember(member)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                    title="Edit member role"
                  >
                    <SafeIcon icon={FiEdit3} />
                  </button>
                  <button
                    onClick={() => handleDeleteMember(member)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Remove member"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Pending Invitations</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex flex-col md:flex-row items-start md:items-center justify-between p-4 border border-orange-200 bg-orange-50 rounded-lg space-y-4 md:space-y-0"
                >
                  <div className="flex items-center space-x-4">
                    <SafeIcon icon={FiMail} className="text-2xl text-orange-600" />
                    <div>
                      <p className="font-medium text-gray-900">{invitation.email}</p>
                      <p className="text-sm text-gray-600">
                        Invited by {invitation.invited_by_user?.full_name || 'Admin'} •{' '}
                        {new Date(invitation.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Expires: {new Date(invitation.expires_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(invitation.role)}`}>
                      {invitation.role.toUpperCase()}
                    </span>
                    <button
                      onClick={() => handleCopyInvitationLink(invitation.token)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Copy invitation link"
                    >
                      <SafeIcon icon={FiCopy} />
                    </button>
                    <button
                      onClick={() => handleResendInvitation(invitation.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded"
                      title="Resend invitation"
                    >
                      <SafeIcon icon={FiRefreshCw} />
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Cancel invitation"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <InviteFormModal
          onSubmit={handleInvite}
          onCancel={() => setShowInviteForm(false)}
        />
      )}

      {/* Edit Member Modal */}
      {showEditMemberModal && selectedMember && (
        <EditMemberModal
          member={selectedMember}
          onSubmit={handleUpdateMemberRole}
          onCancel={() => {
            setShowEditMemberModal(false)
            setSelectedMember(null)
          }}
        />
      )}

      {/* Delete Member Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteMemberModal && selectedMember !== null}
        onClose={() => {
          setShowDeleteMemberModal(false)
          setSelectedMember(null)
          setMemberDependencies({})
        }}
        onConfirm={confirmDeleteMember}
        itemType="Team Member"
        itemName={selectedMember?.full_name || selectedMember?.email || ''}
        dependencies={memberDependencies}
        loading={deleteLoading}
      />
    </div>
  )
}

// Invite Form Modal Component
const InviteFormModal = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    role: 'user'
  })
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(formData.email, formData.role)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Invite Team Member</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="colleague@example.com"
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={submitting}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.role === 'admin'
                ? 'Admins can manage team members and settings'
                : 'Users can view and edit data but cannot manage team'}
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// Edit Member Modal Component
const EditMemberModal = ({ member, onSubmit, onCancel }) => {
  const [selectedRole, setSelectedRole] = useState(member.role)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit(selectedRole)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Member Role</h2>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Member: <span className="font-medium">{member.auth_users?.full_name || 'Unknown'}</span>
          </p>
          <p className="text-sm text-gray-500">{member.auth_users?.email}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={submitting}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {selectedRole === 'admin'
                ? 'Admins can manage team members and settings'
                : 'Users can view and edit data but cannot manage team'}
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

export default TeamManagement